import { useState, MouseEvent, Fragment } from 'react';
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Modal from '@mui/material/Modal';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import FFPaper from '../../../components/shared/FFPaper';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import preloader from '../../../assets/img/preloader.gif';
import spinner from '../../../assets/img/spinner.gif';
import profilePic from '../../../assets/img/profile.png';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import AddEnrollmentDetailsTab from '../../../components/dashboard/enrollment/AddEnrollmentDetailsTab';
import AddEnrollmentAdditionalInfoTab from '../../../components/dashboard/enrollment/AddEnrollmentAdditionalInfoTab';


export default function AddEnrollmentModal({ open, closeEnrollmentModal }: { open: boolean, closeEnrollmentModal: () => void }) {
  const theme = useTheme();

  const [activeTab, setActiveTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [profileImage, setProfileImage] = useState('');
  const [identifying, setIdentifying] = useState(false);

  interface Identification {
    exposure: number;
    sharpFace: number;
    resolution: number;
    noHotSpots: number;
  }
  const [identification, setIdentification] = useState<undefined | Identification>(undefined);


  const uploadFile = (event: MouseEvent<HTMLElement>) => {
    setUploading(true);
    setTimeout(() => {
      console.log('set profile Image');
      setProfileImage(profilePic);
    }, 5000);
  };

  const identify = () => {
    setIdentifying(true);

    setTimeout(() => {
      const identified: Identification = {
        exposure: 0.9,
        sharpFace: 0.74,
        resolution: 0.79,
        noHotSpots: 0
      };
      setIdentifying(false);
      setIdentification(identified);
    }, 5000);
  }

  return (
    <Modal
      open={open}
      onClose={closeEnrollmentModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <FFPaper
        elevation={3}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '96%',
          height: '96%',
          p: 0,
          borderColor: 'transparent'
        }}
      >
        <Stack direction="row"
          sx={{
            justifyContent: 'space-between',
          }}
        >

          <Typography variant='h5' color="primary"
            sx={{
              fontWeight: 'bold',
              mt: 4,
              ml: 3
            }}>
            Add New Enrollment
          </Typography>


          <IconButton
            onClick={closeEnrollmentModal}
            sx={{
              mr: 2,
              mt: 2,
            }}
            color="primary" aria-label="upload picture" component="span">
            <ClearIcon
              sx={{
                fontSize: '50px',
                color: 'info.main'
              }}
            />

          </IconButton>

        </Stack>

        <Divider sx={{ mt: 3 }} />
        <Stack direction="row" divider={<Divider orientation="vertical" flexItem={true} />}
          sx={{
            height: '100%',
            width: '100%'
          }}
        >

          {/*Beginning of Left Box> */}
          <Stack

            sx={{
              flex: '1 0 40%',
              p: 3
            }}
          >
            <Stack direction="row"
              sx={{
                justifyContent: 'flex-start',
                width: '80%'
              }}
              spacing={1}>
              <Typography
                sx={{
                  flex: '1 0 15%',
                  textAlign: 'right'
                }}
                variant="h5" color="secondary">
                1.
              </Typography>

              <Box
                sx={{
                  flex: '1 0 85%',
                  textAlign: 'center'
                }}
              >
                <Box
                  sx={{
                    border: '2px dashed secondary.main',
                    borderRadius: 2,
                    width: 500,
                    height: 500,
                    backgroundColor: 'shadow.light',
                    ml: 'auto',
                    mr: 'auto',
                    pt: 0,
                    pb: 0,
                    pr: 0,
                    pl: 0
                  }}
                >
                  <Stack
                    sx={{
                      height: '100%',
                      justifyContent: 'space-evenly',
                      alignItems: 'center',
                    }}
                  >
                    {!uploading && <Fragment>
                      <Typography
                        color="secondary"
                        sx={{
                          textAlign: 'center'
                        }}
                      >
                        Drag and drop a photo to identify here
                      </Typography>
                      <Divider color="primary"
                        sx={{
                          width: '90%',
                          borderColor: 'shadow.dark'
                        }}
                      />
                      <Button
                        variant="contained" color="secondary"
                        onClick={uploadFile}
                        sx={{
                          width: '50%',
                        }}
                      >Browse Files</Button>

                    </Fragment>
                    }

                    {uploading && !profileImage && <Avatar sx={{ width: 200, height: 200 }} src={preloader} />}

                    {uploading && profileImage && <Avatar variant="square" sx={{ width: 500, height: 500 }} src={profileImage} />}

                  </Stack>

                </Box>

                <Typography
                  color="primary"
                  sx={{
                    textAlign: 'center',
                    pt: 1
                  }}>
                  Select a photo of an individual to identify and enroll
                </Typography>

                <Stack direction="row"
                  sx={{
                    justifyContent: 'center',
                    mt: 4
                  }}
                  spacing={2}
                >
                  <Button
                    sx={{
                      background: 'white 0% 0% no-repeat padding-box',
                      boxShadow: '0px 0px 8px ' + theme.palette.background.default,
                      border: 0
                    }}
                    variant="outlined" color="secondary"
                    size="large"
                    disabled={profileImage ? false : true}
                    startIcon={<AddIcon fontSize="large" />}
                  >Reselect Photo</Button>
                  <Button
                    sx={{
                      background: 'white 0% 0% no-repeat padding-box',
                      boxShadow: '0px 0px 8px ' + theme.palette.background.default,
                      border: 0
                    }}
                    variant="outlined" color="secondary"
                    size="large"
                    disabled={profileImage ? false : true}
                    startIcon={<AddIcon fontSize="large" />}
                  >Crop Photo</Button>

                </Stack>

                <Button
                  variant="contained" color="secondary"
                  size="large"
                  disabled={profileImage ? false : true}

                  sx={{
                    mt: 4
                  }}
                  onClick={identify}
                >Identify & Enroll</Button>

                {identifying && <Stack
                  sx={{
                    mt: 4,
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}
                >
                  <Typography color="secondary" variant="h5">Identifying Photo</Typography>
                  <Avatar sx={{ width: 200, height: 200 }} src={spinner} />
                </Stack>
                }

                {identification && <Stack
                  sx={{
                    mt: 4,
                    display: 'flex',
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    textAlign: 'center'
                  }}
                >
                  <Stack direction="row" spacing={3} sx={{ p: 1 }}>
                    <CheckCircleIcon
                      htmlColor="#66AA66"
                    />
                    <Typography
                      sx={{
                        color: 'text.primary'
                      }}
                    >EXPOSURE</Typography>
                    <Typography
                      color="secondary"
                    >0.90</Typography>
                  </Stack>

                  <Stack direction="row" spacing={3} sx={{ p: 1 }}>
                    <CheckCircleIcon
                      htmlColor="#66AA66"
                    />
                    <Typography
                      sx={{
                        color: 'text.primary'
                      }}
                    >SHARP FACE</Typography>
                    <Typography
                      color="secondary"
                    >0.90</Typography>
                  </Stack>

                  <Stack direction="row" spacing={3} sx={{ p: 1 }}>
                    <CheckCircleIcon
                      htmlColor="#66AA66"
                    />
                    <Typography
                      sx={{
                        color: 'text.primary'
                      }}
                    >RESOLUTION</Typography>
                    <Typography
                      color="secondary"
                    >0.90</Typography>
                  </Stack>

                  <Stack direction="row" spacing={3} sx={{ p: 1 }}>
                    <ErrorRoundedIcon
                      htmlColor="#E31F1F"
                    />
                    <Typography
                      sx={{
                        color: 'text.primary'
                      }}
                    >NO HOT SPOTS</Typography>
                    <Typography
                      color="secondary"
                    >0.90</Typography>
                  </Stack>
                </Stack>
                }


              </Box>


            </Stack>

          </Stack>

          {/*End of Left Box> */}

          <Stack
            sx={{
              flex: '1 0 60%',
              pt: 3,
              pb: 3
            }}
          >

            <Stack direction="row"
              sx={{
                borderBottom: '1px solid lightgray',
                pl: 2
              }}
            >
              <Typography variant="h5" color="secondary">
                2.
              </Typography>
              <Box sx={{
                ml: 3,
                mr: 3,
                borderBottom: activeTab === 0 ? '4px solid secondary.main': 0
              }}>
                <Button variant="text" color="secondary"
                  sx={{
                    color: 'secondary.main'
                  }}
                  onClick={() => setActiveTab(0)}
                >
                  Enrollment Details
                </Button>
              </Box>
              <Box sx={{
                ml: 3,
                mr: 3,
                borderBottom: activeTab === 1 ? '4px solid secondary.main': 0
              }}>
                <Button variant="text" color="secondary"
                  sx={{
                    color: 'text.secondary'
                  }}
                  onClick={() => setActiveTab(1)}
                >
                  Additional Information
                </Button>
              </Box>
            </Stack>

            {activeTab === 0 && <AddEnrollmentDetailsTab />}
            
            {activeTab === 1 && <AddEnrollmentAdditionalInfoTab />}

          </Stack>
        </Stack>
      </FFPaper>
    </Modal>
  );
}